import React from "react";

import { CloseOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import { Breadcrumb } from "antd";

import StyledTooltip from "./StyledTooltip";

import "./PageBreadcrumb.css";

function getItemLabel(item) {
  if (item == null) return "";
  if (typeof item === "string") return item;
  return item.label || "";
}

function renderTrail(trailItems, separator, keyPrefix) {
  if (!trailItems.length) return null;

  return (
    <Breadcrumb separator={separator}>
      {trailItems.map((item, index) => (
        <Breadcrumb.Item key={`${keyPrefix}-${getItemLabel(item)}-${index}`}>
          {getItemLabel(item)}
        </Breadcrumb.Item>
      ))}
    </Breadcrumb>
  );
}

function handleActivate(handler) {
  return (event) => {
    if (typeof handler !== "function") return;
    if (event.type === "keydown" && event.key !== "Enter" && event.key !== " ") return;
    handler();
  };
}

export default function PageBreadcrumb({
  items = [],
  extraItems = [],
  titleOnly = false,
  title = "",
  showBack = true,
  onBack,
  backTooltip = "Back",
  showForward = false,
  onForward,
  forwardTooltip = "Forward",
  forwardDisabled = false,
  showClose = false,
  onClose,
  closeTooltip = "Go to Main Page",
  separator = "/",
  groupSeparator,
  variant = "default",
  className = "",
  ariaLabel = "Breadcrumb",
}) {
  const trailItems = Array.isArray(items) ? items.filter(Boolean) : [];
  const suffixItems = Array.isArray(extraItems) ? extraItems.filter(Boolean) : [];
  const titleOnlyLabel = title || getItemLabel(trailItems[trailItems.length - 1]);
  const variantClass = variant !== "default" ? ` PageBreadcrumb--${variant}` : "";

  return (
    <nav
      className={`PageBreadcrumb${variantClass} ${className}`.trim()}
      aria-label={ariaLabel}
    >
      {(showBack || showForward) && (
        <div className="PageBreadcrumb__NavIcons">
          {showBack && (
            <StyledTooltip title={backTooltip} placement="top">
              <LeftOutlined
                role="button"
                tabIndex={0}
                className="PageBreadcrumb__NavIcon"
                aria-label={backTooltip}
                onClick={handleActivate(onBack)}
                onKeyDown={handleActivate(onBack)}
              />
            </StyledTooltip>
          )}
          {showForward && (
            <StyledTooltip title={forwardDisabled ? "" : forwardTooltip} placement="top">
              <RightOutlined
                role="button"
                tabIndex={forwardDisabled ? -1 : 0}
                aria-disabled={forwardDisabled}
                aria-label={forwardTooltip}
                className={`PageBreadcrumb__NavIcon${
                  forwardDisabled ? " PageBreadcrumb__NavIcon--disabled" : ""
                }`}
                onClick={forwardDisabled ? undefined : handleActivate(onForward)}
                onKeyDown={forwardDisabled ? undefined : handleActivate(onForward)}
              />
            </StyledTooltip>
          )}
        </div>
      )}

      <div className={`PageBreadcrumb__Trail${titleOnly ? " PageBreadcrumb__Trail--titleOnly" : ""}`}>
        {titleOnly ? (
          <>
            <div className="PageBreadcrumb__Accent" aria-hidden />
            <span className="PageBreadcrumb__Title">{titleOnlyLabel}</span>
          </>
        ) : (
          <>
            {renderTrail(trailItems, separator, "trail")}
            {suffixItems.length > 0 && (
              <>
                {groupSeparator && (
                  <span className="PageBreadcrumb__TrailSep" aria-hidden>
                    {groupSeparator}
                  </span>
                )}
                {renderTrail(suffixItems, separator, "extra")}
              </>
            )}
          </>
        )}
      </div>

      {showClose && (
        <StyledTooltip title={closeTooltip} placement="top">
          <CloseOutlined
            role="button"
            tabIndex={0}
            className="PageBreadcrumb__CloseIcon"
            aria-label={closeTooltip}
            onClick={handleActivate(onClose)}
            onKeyDown={handleActivate(onClose)}
          />
        </StyledTooltip>
      )}
    </nav>
  );
}
